<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  AVAILABLE_TOURS,
  tutorialDialogDismissedThisSession,
  useTourAccess,
  type TourId,
} from "@/composables/shared/useProductTour";
import { useSettingsStore } from "@/stores/settingsStore";
import { EPracticeStaffSettingsKey } from "@/api/models/practice/practiceStaffSettings";
import BaseDialog from "@/components/shared/dialogs/BaseDialog.vue";
import BaseDialogActions from "@/components/shared/dialogs/BaseDialogActions.vue";

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", v: boolean): void;
  (e: "start", tourId: TourId): void;
}>();

const { t } = useI18n();
const settingsStore = useSettingsStore();
const { canStartTourId } = useTourAccess();

const isOpen = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit("update:modelValue", v),
});

const storeRecord = settingsStore as unknown as Record<string, unknown>;

const isTourCompleted = (tour: (typeof AVAILABLE_TOURS)[number]) =>
  Boolean(storeRecord[tour.completionKey]);

const accessibleTours = computed(() => AVAILABLE_TOURS.filter((tour) => canStartTourId(tour.id)));

const areAllToursCompleted = computed(() =>
  accessibleTours.value.every((tour) => isTourCompleted(tour))
);

const firstAvailableTourId = computed((): TourId | null => {
  if (areAllToursCompleted.value) return null;

  return (
    accessibleTours.value.find((tour) => !isTourCompleted(tour))?.id ??
    accessibleTours.value[0]?.id ??
    null
  );
});

const selectedTourId = ref<TourId | null>(firstAvailableTourId.value);

const actionButtonText = computed(() => {
  if (!selectedTourId.value) return "";

  const tour = AVAILABLE_TOURS.find((tour) => tour.id === selectedTourId.value);
  if (!tour) return "";

  const key = areAllToursCompleted.value
    ? "generic.tour.tourList.replayNamed"
    : "generic.tour.tourList.startNamed";

  return t(key, { name: t(tour.titleKey) });
});

watch(isOpen, (open) => {
  if (open) {
    selectedTourId.value = firstAvailableTourId.value;
  }
});

const handleStart = () => {
  if (!selectedTourId.value || !canStartTourId(selectedTourId.value)) return;
  emit("start", selectedTourId.value);
  isOpen.value = false;
};

const handleSkip = () => {
  const currentIndex = AVAILABLE_TOURS.findIndex((tour) => tour.id === selectedTourId.value);
  const next = AVAILABLE_TOURS.slice(currentIndex + 1).find(
    (tour) => canStartTourId(tour.id) && !isTourCompleted(tour)
  );

  if (next) {
    selectedTourId.value = next.id;
  } else {
    handleDismiss();
  }
};

const handleDismiss = () => {
  tutorialDialogDismissedThisSession.value = true;
  settingsStore[EPracticeStaffSettingsKey.TUTORIAL_DIALOG_SEEN] = true;
  isOpen.value = false;
};
</script>

<template>
  <BaseDialog
    v-model="isOpen"
    transition="dialog-transition"
    data-testid="tutorial-select-dialog"
    :icon="areAllToursCompleted ? 'mdi-check-circle' : 'mdi-help'"
    @close="handleDismiss"
  >
    <template #title>
      {{
        areAllToursCompleted
          ? t("generic.tour.tourList.allDoneTitle")
          : t("generic.tour.tourList.title")
      }}
    </template>
    <template #subtitle>
      {{
        areAllToursCompleted
          ? t("generic.tour.tourList.allDoneSubtitle")
          : t("generic.tour.tourList.subtitle")
      }}
    </template>
    <template #body>
      <div class="tw:flex tw:flex-col tw:gap-3">
        <div
          v-for="tour in accessibleTours"
          :key="tour.id"
          class="tw:flex tw:items-center tw:gap-4 tw:rounded-xl tw:border-2 tw:transition-all tw:duration-200"
          :class="
            selectedTourId === tour.id
              ? 'tw:border-secondary tw:bg-secondary/10 tw:cursor-pointer'
              : isTourCompleted(tour)
                ? 'tw:border-liteDef5 tw:bg-surface-muted tw:cursor-pointer'
                : 'tw:border-liteDef5 tw:bg-surface hover:tw:border-liteDef5 tw:cursor-pointer'
          "
          style="padding: 1rem 1.25rem"
          @click="selectedTourId = tour.id"
        >
          <div
            class="tw:shrink-0 tw:w-12 tw:h-12 tw:flex tw:items-center tw:justify-center tw:overflow-hidden"
          >
            <v-icon
              :icon="tour.icon"
              size="28"
              :color="
                selectedTourId === tour.id
                  ? 'secondary'
                  : settingsStore.isDarkMode
                    ? 'grey-lighten-1'
                    : 'grey-darken-2'
              "
            />
          </div>

          <div class="tw:flex-1 tw:min-w-0">
            <div class="tw:flex tw:items-center tw:gap-1.5">
              <span class="tw:text-base tw:font-semibold tw:text-on-surface">
                {{ t(tour.titleKey) }}
              </span>
            </div>
            <div class="tw:text-sm tw:text-on-surface/60 tw:mt-0.5">
              {{ t(tour.descriptionKey) }}
            </div>
          </div>

          <div class="tw:shrink-0">
            <v-icon
              v-if="isTourCompleted(tour)"
              icon="mdi-check-circle"
              :color="selectedTourId === tour.id ? 'success' : 'grey-lighten-1'"
              size="20"
            />
            <v-radio
              v-else
              :model-value="selectedTourId"
              :value="tour.id"
              color="secondary"
              hide-details
              density="compact"
              @click.stop="selectedTourId = tour.id"
            />
          </div>
        </div>
      </div>
    </template>
    <template #actions>
      <BaseDialogActions
        v-if="areAllToursCompleted && !selectedTourId"
        :primary="{
          icon: 'mdi-close',
          action: handleDismiss,
          text: t('generic.action.close'),
        }"
      />
      <BaseDialogActions
        v-else-if="areAllToursCompleted && selectedTourId"
        :secondary="{
          text: t('generic.action.close'),
          action: handleDismiss,
        }"
        :primary="{
          action: handleStart,
          text: actionButtonText,
        }"
      />
      <BaseDialogActions
        v-else
        :secondary="{
          text: t('generic.tour.tourList.skipForNow'),
          action: handleSkip,
        }"
        :primary="{
          action: handleStart,
          disabled: !selectedTourId || !canStartTourId(selectedTourId),
          text: actionButtonText,
        }"
      />
    </template>
  </BaseDialog>
</template>
